import React, { useEffect, useRef, useState } from "react";
import classNames from "classnames";
import { Link, NavLink, useLocation } from "react-router-dom";
import { ReactComponent as BillBioLogo } from "../../../assets/NewLogo.svg";
import { ReactComponent as BillBioLogoDark } from "../../../assets/NewLogoDark.svg";
import { ReactComponent as GlobeIcon } from "../../../assets/landingPages/header/globe.svg";
import { ReactComponent as GlobeIconDark } from "../../../assets/landingPages/header/globeDark.svg";
import { ReactComponent as UserDummyIcon } from "../../../assets/landingPages/header/userDummyIcon.svg";
import useLocales from "../../../hooks/useLocales";
import useSettings from "../../../hooks/useSettings";
import { DarkModeSwitch } from "react-toggle-dark-mode";
import { useDispatch, useSelector } from "react-redux";
import { toggleDarkMode } from "../../../redux/slices/darkThemeSlice/themSlice";
import ProtectedButton from "../../../guards/ProtectedButton";

const activeNav =
  "dark:text-white text-main_color block py-2 pl-3 pr-4 lg:p-0 hover:text-main_color text-body_copy1_b -tracking-[0.5px]";
const normalNav =
  "dark:text-white block py-2 pl-3 pr-4 lg:p-0 hover:text-main_color text-body_copy1";
const ImageUrl = process.env.REACT_APP_API_URL;
const Navbar = () => {
  const { darkMode } = useSelector((state) => state.theme);
  const dispatch = useDispatch();

  // Using React useRef to create references for DOM elements
  const iconRef = useRef(); // Reference for icon element
  const iconMRef = useRef(); // Reference for mobile icon element
  const modalRef = useRef(); // Reference for modal element
  const modalMRef = useRef(); // Reference for mobile modal element

  // Getting the current location and theme direction from the environment
  const location = useLocation();
  const { themeDirection } = useSettings();

  // Getting language-related data and functions from the environment
  const { allLangs, translate, onChangeLang, handleLanguageChange } =
    useLocales();

  // State variables for managing the header behavior
  const [scrolled, setScrolled] = useState(); // Variable to track if the user has scrolled
  const [isOpen, setIsOpen] = useState(); // Variable to manage the open/close state of a menu
  const [isOpenLangModal, setIsOpenLangModal] = useState(false); // Variable to manage the language modal open/close state
  const [isOpenLangMModal, setIsOpenLangMModal] = useState(false); // Variable to manage the mobile language modal open/close state
  const [darkSide, setDarkSide] = useState(darkMode);

  const { isInvitationActive = false } = useSelector(
    (state) => state?.invitationListEmails
  );
  const users = localStorage.getItem("user")
    ? JSON.parse(localStorage.getItem("user"))
    : null;

  const userToken = users?.token;
  //Dark and Light mode toggling  function
  const toggleMode = (checked) => {
    dispatch(toggleDarkMode());
    const root = window.document.documentElement;
    root.classList.remove(checked ? "light" : "dark");
    root.classList.add(checked ? "dark" : "light");
    setDarkSide(checked);
  };

  // Creating CSS classes based on the state
  const classes = classNames("header", {
    scrolled: scrolled, // Adding 'scrolled' class based on the 'scrolled' state
  });
  const sideMenuClasses = classNames("sideMenu", {
    isOpen: isOpen, // Adding 'isOpen' class based on the 'isOpen' state
    isNotOpen: !isOpen, // Adding 'isNotOpen' class based on the negation of 'isOpen' state
  });

  // Adding an event listener to handle clicks outside the language modal
  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (
        isOpenLangMModal &&
        modalMRef.current &&
        !modalMRef.current.contains(e.target) &&
        !iconMRef.current.contains(e.target)
      ) {
        setIsOpenLangMModal(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [isOpenLangMModal]);

  // Adding an event listener to handle clicks outside the language modal (for desktop)
  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (
        isOpenLangModal &&
        modalRef.current &&
        !modalRef.current.contains(e.target) &&
        !iconRef.current.contains(e.target)
      ) {
        setIsOpenLangModal(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [isOpenLangModal]);

  // Adding a scroll event listener to set the navbar as sticky when scrolling

  useEffect(() => {
    const handleScroll = (_) => {
      if (window.pageYOffset > 1) {
        setScrolled(true); // Set 'scrolled' to true when the user has scrolled
      } else {
        setScrolled(false); // Set 'scrolled' to false when the user is at the top
      }
    };
    window.addEventListener("scroll", handleScroll);

    return (_) => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  // Getting user data from the Redux store
  const user = useSelector((state) => state?.user?.user);

  // Resetting the 'isOpen' state when the location (pathname) changes
  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  // Function to change the language and close the language modal
  const handleChangeLang = (newLang) => {
    onChangeLang(newLang); // Change the language
    // handleLanguageChange(newLang);
    setIsOpenLangModal(false); // Close the language modal
    setIsOpenLangMModal(false); // Close the mobile language modal
  };

  // Getting user profile data from the Redux store
  const user_data = useSelector(
    (state) => state?.adminUser?.viewUserData?.user_data
  );

  // State variable to store the window width
  const [WinW, setWinW] = useState(window.innerWidth);

  // Adding a resize event listener to update the window width
  useEffect(() => {
    const handleResize = () => {
      setWinW(window.innerWidth); // Update the window width
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  // Adding an event listener to handle resizing (for closing the mobile menu when the screen size changes)
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 1024) {
        setIsOpen(false);
      }
    };

    window.addEventListener("resize", handleResize); // Adding event listener for resize

    return () => {
      window.removeEventListener("resize", handleResize); // Removing event listener on component unmount
    };
  }, []); // Dependency array is empty, so this effect only runs once on component mount

  return (
    <>
      <div
        style={{
          zIndex: "99999",
          backgroundColor: darkMode ? "#090909" : "#ffffff",
        }}
        className={`fixed w-screen top-0 h-screen overflow-hidden  bg-[#FBF7F7CC] dark:bg-dark-primary backdrop-blur-md border p-6 ${sideMenuClasses}`}
      >
        <div
          className={`flex justify-between items-center ${
            themeDirection === "rtl" ? "flex-row-reverse" : ""
          }`}
        >
          <div onClick={() => setIsOpen(false)}>
            <Link to={"/"} className="flex items-center w-[80px] sm:w-[121px]">
              {darkMode ? (
                <BillBioLogoDark className="w-[100%] sm:w-[100%] h-auto" />
              ) : (
                <BillBioLogo className="w-[100%] sm:w-[100%] h-auto" />
              )}
            </Link>
          </div>
          <button className="p-0.5 text-xl" onClick={() => setIsOpen(false)}>
            {darkMode ? (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                fill="#ffffff"
                className="bi bi-x-square-fill"
                viewBox="0 0 16 16"
              >
                <path d="M2 0a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V2a2 2 0 0 0-2-2zm3.354 4.646L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 1 1 .708-.708" />
              </svg>
            ) : (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                fill="currentColor"
                className="bi bi-x-square"
                viewBox="0 0 16 16"
              >
                <path d="M14 1a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1H2a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1zM2 0a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V2a2 2 0 0 0-2-2z" />
                <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708" />
              </svg>
            )}
          </button>
        </div>
        <div className="h-full w-full flex flex-col mt-[30px] ">
          <ul className="flex flex-col gap-5">
            <li
              onClick={() => setIsOpen(false)}
              className="bg-[#F6F0E4] dark:bg-dark-secondary hover:bg-[#F6F0E4AA] dark:hover:bg-dark-secondary rounded"
            >
              {!userToken && isInvitationActive === true ? null : (
                <NavLink
                  to={"about"}
                  className={({ isActive }) =>
                    isActive ? activeNav : normalNav
                  }
                >
                  {translate("header.about")}
                </NavLink>
              )}
            </li>
            <li
              onClick={() => setIsOpen(false)}
              className="bg-[#F6F0E4] dark:bg-dark-secondary hover:bg-[#F6F0E4AA] dark:hover:bg-dark-secondary rounded"
            >
              {!userToken && isInvitationActive === true ? null : (
                <NavLink
                  to={"contact"}
                  className={({ isActive }) =>
                    isActive ? activeNav : normalNav
                  }
                >
                  {translate("header.contact")}
                </NavLink>
              )}
            </li>
            <li
              onClick={() => setIsOpen(false)}
              className="bg-[#F6F0E4] dark:bg-dark-secondary hover:bg-[#F6F0E4AA] dark:hover:bg-dark-secondary rounded"
            >
              {!userToken && isInvitationActive === true ? null : (
                <NavLink
                  to={"/pricing"}
                  className={({ isActive }) =>
                    isActive ? activeNav : normalNav
                  }
                >
                  {translate("header.pricing")}
                </NavLink>
              )}
            </li>
            <li>
              {user?.token ? (
                <Link to={"/mDashboard"}>
                  <div
                    className={`rounded-full pt-[5px] pb-[6px] px-3 bg-light_bg_v5 dark:bg-dark-secondary ${
                      darkMode ? "" : "shadow-shadow_2"
                    } flex items-center gap-2.5`}
                  >
                    <div className="rounded-full">
                      {user_data?.profile_image ? (
                        <img
                          className="h-[36px] max-h-[36px] max-w-[36px] w-[36px] object-cover rounded-full"
                          src={`${ImageUrl}/profile/${user_data?.profile_image}`}
                          alt="profileImage"
                          title={"Profile Pic"}
                        />
                      ) : (
                        <UserDummyIcon height={36} width={36} />
                      )}
                    </div>
                    <p className="text-small1_bold uppercase dark:text-light_bg_v5">
                      {user_data?.pageName ? user_data.pageName : "USER"}
                    </p>
                  </div>
                </Link>
              ) : (
                <div
                  className="flex items-center gap-5"
                  onClick={() => setIsOpen(false)}
                >
                  <NavLink to={"/auth/login"} className="w-full">
                    <button className="w-full rounded-full pt-[9px] pb-[8px] px-[26px] whitespace-nowrap sm:pt-[13px] sm:pb-[12px] sm:px-[40px] text-white bg-main_color text-small2_b sm:text-small1_bold">
                      LOG IN
                    </button>
                  </NavLink>
                  <NavLink to={"/waitinglistuser"} className="w-full">
                    <button className="w-full rounded-full pt-[9px] pb-[8px] px-[26px] whitespace-nowrap sm:pt-[13px] sm:pb-[12px] sm:px-[40px] text-white bg-main_color text-small2_b sm:text-small1_bold">
                      SIGN UP
                    </button>
                  </NavLink>
                </div>
              )}
            </li>
          </ul>
        </div>
      </div>
      <nav
        style={{
          zIndex: "99999",
          backgroundColor: darkMode ? "#090909" : "#ffffff",
        }}
        className={`dark:bg-dark-primary bg-transparent border-gray-200 sticky top-0 ${classes}`}
      >
        {/* mt-5 :- This was navbar top margin to set its sticky behaviour */}
        <div className="flex justify-center ">
          <div className="2xl:max-w-screen-xl xl:max-w-screen-lg w-full">
            <div
              className={` flex flex-wrap ${
                themeDirection === "rtl" ? "flex-row-reverse" : ""
              } items-center justify-between mx-auto 2xl:mx-5 xl:mx-0 smm:px-3 sm:mx-3 lg:mx-5 p-3 `}
            >
              <div
                className={`flex items-center ${
                  themeDirection === "rtl" ? "flex-row-reverse" : ""
                } gap-16`}
              >
                <Link
                  to={"/"}
                  className="w-[80px] sm:w-[121px] flex items-center"
                >
                  {darkMode ? (
                    <BillBioLogoDark className="w-[100%] sm:w-[100%] h-auto" />
                  ) : (
                    <BillBioLogo className="w-[100%] sm:w-[100%] h-auto" />
                  )}
                </Link>
                <div
                  className="hidden w-full lg:block lg:w-auto"
                  id="navbar-default"
                >
                  <ul
                    className={`flex lg:flex-row ${
                      themeDirection === "rtl" ? "lg:flex-row-reverse" : ""
                    } gap-12 lg:mt-1.5 lg:border-0`}
                  >
                    <li>
                      {!userToken && isInvitationActive === true ? null : (
                        <NavLink
                          to={"about"}
                          className={({ isActive }) =>
                            isActive ? activeNav : normalNav
                          }
                        >
                          {/*About */}
                          {translate("header.about")}
                        </NavLink>
                      )}
                    </li>
                    <li>
                      {!userToken && isInvitationActive === true ? null : (
                        <NavLink
                          to={"contact"}
                          className={({ isActive }) =>
                            isActive ? activeNav : normalNav
                          }
                        >
                          {/* Contact */}
                          {translate("header.contact")}
                        </NavLink>
                      )}
                    </li>

                    <li>
                      {!userToken && isInvitationActive === true ? null : (
                        <NavLink
                          to={"/pricing"}
                          className={({ isActive }) =>
                            isActive ? activeNav : normalNav
                          }
                        >
                          {/* Pricing */}
                          {translate("header.pricing")}
                        </NavLink>
                      )}
                    </li>
                  </ul>
                </div>
              </div>
              <div
                className={`flex gap-3 items-center ${
                  themeDirection === "rtl" ? "flex-row-reverse" : ""
                }`}
              >
                {user?.token ? (
                  <Link to={"/mDashboard"} className="lg:hidden">
                    <div
                      className={`rounded-full pt-[3px] pb-[4px] px-2.5 bg-white dark:bg-dark-secondary dark:text-light_bg_v5 ${
                        darkMode ? "" : "shadow-shadow_2"
                      } flex items-center gap-2.5`}
                    >
                      <p className="text-small2_b uppercase">
                        {user_data?.pageName
                          ? WinW < 375
                            ? user_data.pageName?.length > 5
                              ? user_data.pageName?.slice(0, 5) + "..."
                              : user_data.pageName
                            : WinW < 480
                            ? user_data.pageName?.length > 12
                              ? user_data.pageName?.slice(0, 12) + "..."
                              : user_data.pageName
                            : user_data.pageName
                          : "USER"}
                      </p>
                      <div className="rounded-full">
                        {user_data?.profile_image ? (
                          <img
                            className="h-[30px] max-h-[30px] max-w-[30px] w-[30px] object-cover rounded-full"
                            src={`${ImageUrl}/profile/${user_data?.profile_image}`}
                            alt="profileImage"
                            title={"Profile Pic"}
                          />
                        ) : (
                          <UserDummyIcon height={36} width={36} />
                        )}
                      </div>
                    </div>
                  </Link>
                ) : (
                  <NavLink to={"/mDashboard"}>
                    <button className="rounded-full lg:hidden pt-[9px] pb-[8px] px-[26px] whitespace-nowrap sm:pt-[13px] sm:pb-[12px] sm:px-[40px] text-white bg-main_color text-small2_b sm:text-small1_bold">
                      {translate("descriptions.get_started")}
                    </button>
                  </NavLink>
                )}
                <div className="relative lg:hidden">
                  {darkMode ? (
                    <GlobeIconDark
                      onClick={() => setIsOpenLangMModal(!isOpenLangMModal)}
                      className="cursor-pointer h-6 w-6"
                      ref={iconMRef}
                    />
                  ) : (
                    <GlobeIcon
                      onClick={() => setIsOpenLangMModal(!isOpenLangMModal)}
                      className="cursor-pointer h-6 w-6"
                      ref={iconMRef}
                    />
                  )}

                  {isOpenLangMModal && (
                    <div
                      ref={modalMRef}
                      className="absolute rounded-xl bg-white dark:bg-dark-secondary top-11 -right-2.5"
                    >
                      <div className="flex relative rounded-xl w-max flex-col p-2 gap-2">
                        <div className="absolute h-3 w-3 bg-white  dark:bg-dark-secondary top-1.5 right-4 rotate-45"></div>
                        {allLangs.map((lang) => {
                          return (
                            <div
                              key={lang?.value}
                              onClick={() => handleChangeLang(lang?.value)}
                              className={`p-1 px-2 hover:bg-gray-100 dark:hover:bg-dark-primary cursor-pointer rounded-lg ${
                                themeDirection === "rtl"
                                  ? "flex-row-reverse"
                                  : ""
                              } flex items-center gap-2 min-w-fit`}
                            >
                              <p className="whitespace-nowrap dark:text-color_v1_dark">
                                {lang?.label}
                              </p>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
                <div className="relative lg:hidden">
                  <DarkModeSwitch
                    checked={darkSide}
                    onChange={toggleMode}
                    size={30}
                  />
                </div>
                <button
                  // onClick={() => setIsOpen(true)}
                  onClick={() => setIsOpen((prevIsOpen) => !prevIsOpen)}
                  data-collapse-toggle="navbar-default"
                  type="button"
                  className="inline-flex items-center p-0.5 px-0 text-sm text-gray-500 rounded-lg lg:hidden hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-200 dark:text-gray-400 dark:hover:bg-gray-700 dark:focus:ring-gray-600"
                  aria-controls="navbar-default"
                  aria-expanded="false"
                >
                  <span className="sr-only">Open main menu</span>
                  {darkMode ? (
                    <svg
                      className="w-6 h-6"
                      aria-hidden="true"
                      fill="#fff"
                      viewBox="0 0 20 20"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      {" "}
                      <path
                        fillRule="evenodd"
                        d="M3 5a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 10a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 15a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z"
                        clipRule="evenodd"
                      ></path>
                    </svg>
                  ) : (
                    <svg
                      className="w-6 h-6"
                      aria-hidden="true"
                      fill="#292D32"
                      viewBox="0 0 20 20"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        fillRule="evenodd"
                        d="M3 5a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 10a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 15a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z"
                        clipRule="evenodd"
                      ></path>
                    </svg>
                  )}
                </button>
              </div>
              <div
                className="hidden w-full lg:block lg:w-auto"
                id="navbar-default"
              >
                <div
                  className={`flex items-center gap-5 ${
                    themeDirection === "rtl" ? "flex-row-reverse" : ""
                  }`}
                >
                  {user?.token ? (
                    <Link to={"/dashboard"}>
                      <div
                        className={`rounded-full pt-[5px] pb-[6px] px-6 bg-white  dark:bg-dark-secondary dark:text-light_bg_v5 ${
                          darkMode ? "" : "shadow-shadow_2"
                        } flex items-center ${
                          themeDirection === "rtl" ? "flex-row-reverse" : ""
                        } gap-2.5 `}
                      >
                        <p className="text-small1_bold uppercase">
                          {user_data?.pageName ? user_data.pageName : "USER"}
                        </p>
                        <div className="rounded-full">
                          {user_data?.profile_image ? (
                            <img
                              className="h-[36px] max-h-[36px] max-w-[36px] w-[36px] object-cover rounded-full"
                              src={`${ImageUrl}/profile/${user_data?.profile_image}`}
                              alt="profileImage"
                              title={"Profile Pic"}
                            />
                          ) : (
                            <UserDummyIcon height={36} width={36} />
                          )}
                        </div>
                      </div>
                    </Link>
                  ) : (
                    <div
                      className={`flex items-center gap-5 ${
                        themeDirection === "rtl" ? "flex-row-reverse" : ""
                      }`}
                    >
                      <NavLink
                        to={"/auth/login"}
                        className="text-small1_bold  dark:text-light_bg_v5 -tracking-[0.3px]"
                      >
                        LOG IN
                      </NavLink>
                      <NavLink to={"/auth/signUp"}>
                        <button className="rounded-full -tracking-[0.2px] hidden lg:block pt-[9px] pb-[8px] px-[26px] whitespace-nowrap sm:pt-[13px] sm:pb-[12px] sm:px-[40px] text-white bg-main_color text-small2_b sm:text-small1_bold">
                          SIGN UP
                        </button>
                      </NavLink>
                    </div>
                  )}
                  <div className="relative">
                    {darkMode ? (
                      <GlobeIconDark
                        onClick={() => setIsOpenLangModal(!isOpenLangModal)}
                        className="cursor-pointer"
                        ref={iconRef}
                      />
                    ) : (
                      <GlobeIcon
                        onClick={() => setIsOpenLangModal(!isOpenLangModal)}
                        className="cursor-pointer"
                        ref={iconRef}
                      />
                    )}
                    {isOpenLangModal && (
                      <div
                        ref={modalRef}
                        className="absolute rounded-xl bg-white  dark:bg-dark-secondary top-11 -right-2.5"
                      >
                        <div className="flex relative rounded-xl w-max flex-col p-2 gap-2">
                          <div className="absolute h-3 w-3 bg-white  dark:bg-dark-secondary -top-1.5 right-4 rotate-45"></div>
                          {allLangs.map((lang) => {
                            return (
                              <div
                                key={lang?.value}
                                onClick={() => handleChangeLang(lang?.value)}
                                className={`p-1 px-2 hover:bg-gray-100 dark:hover:bg-dark-primary cursor-pointer rounded-lg ${
                                  themeDirection === "rtl"
                                    ? "flex-row-reverse"
                                    : ""
                                } flex items-center gap-2 min-w-fit`}
                              >
                                <p className="whitespace-nowrap dark:text-light_bg_v5">
                                  {lang?.label}
                                </p>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="relative">
                    <DarkModeSwitch
                      checked={darkSide}
                      onChange={toggleMode}
                      size={30}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <style jsx="true">{`
          .header {
            transition: background-color 0.2s;
          }
          .header.scrolled {
            background-color: #fff;
            backdrop-filter: blur(9px);
          }
          .sideMenu {
            transition: transform 0.7s;
          }
          .sideMenu.isOpen {
            transform: translate(0%);
          }
          .sideMenu.isNotOpen {
            transform: translate(100%);
          }
        `}</style>
      </nav>
    </>
  );
};

export default Navbar;
